import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SkeletonTable from "../../components/table/SkeletonTable";
import { employeReport } from "../../api/request/team";
import moment from "moment";
import columns from "../../columns/team/employeReportColumn";
import UserAvatar from "../../components/common/UserAvatar";
import ViewDocModal from "../files/components/ViewDocModal";
import { Select } from "antd";
import YearPicker from "../../components/common/YearPicker";
import Search from "../../components/common/Search";

const EmployeeReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [employeeData, setEmployeeData] = useState(null);
  const [stats, setStats] = useState({});
  const [recentFiles, setRecentFiles] = useState([]);
  const [selectedFileForAckDocs, setSelectedFileForAckDocs] = useState(null);
  const [isDocOpen, setIsDocOpen] = useState(false);

  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [selectedService, setSelectedService] = useState(null);
  const [fileStatus, setFileStatus] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchEmployeeReport();
  }, [id]);

  const fetchEmployeeReport = async (filters = {}, searchText) => {
    setIsLoading(true);
    try {
      const params = { employee_id: id, search: searchText, page: currentPage };

      if (filters?.year) params.year = filters?.year;
      if (filters?.status) params.status = filters?.status;
      if (filters?.file_status) params.file_status = filters?.file_status;

      const { data } = await employeReport(params);
      if (data?.data) {
        const emp = data?.data;

        setEmployeeData(emp?.employee_info);
        setStats({
          total_clients: emp?.total_clients,
          total_file_counts: emp?.total_file_counts,
          paid_invoice: emp?.paid_invoice,
          unpaid_invoice: emp?.unpaid_invoice,
          total_invoice: emp?.total_invoice,
          inprocess_files_count: emp?.inprocess_files_count,
          closed_files_count: emp?.closed_files_count,
          total_files_count: emp?.total_files_count,
          unpaid_interac_e_transfer_payments:
            emp?.unpaid_interac_e_transfer_payments,
          paid_interac_e_transfer_payments:
            emp?.paid_interac_e_transfer_payments,
          total_interac_e_transfer_payments:
            emp?.total_interac_e_transfer_payments,
          completed_tasks_count: emp?.completed_tasks_count,
          running_tasks_count: emp?.running_tasks_count,
        });

        setRecentFiles(
          (emp?.latest_assigned_files || [])?.map((item) => {
            const runningTasks =
              item.tasks
                ?.filter((t) => t.task_status === "running")
                .map((t) => t?.task_name)
                .join(", ") || "-";

            const completedTasks =
              item.tasks
                ?.filter((t) => t.task_status === "complete")
                .map((t) => t.task_name)
                .join(", ") || "-";

            return {
              ...item,
              userDetails: (
                <UserAvatar
                  data={{
                    name: item.file?.client?.name,
                    email: item.file?.client?.email,
                    profile_image: item.file?.client?.profile_image,
                  }}
                />
              ),
              file_number: item.file?.file_number || "-",
              service_name: item.file?.service_type?.service_name || "-",
              file_status: item.file?.status || "-",
              is_submit: item.file?.is_submit || 0,
              running_task: runningTasks,
              completed_task: (
                <p className="w-[200px] truncate" title={completedTasks}>
                  {completedTasks}
                </p>
              ),

              acknowledge_ment_no: item.file?.ack_no || "--",
              created_at: moment(item.file?.created_at).format("lll") || "--",
              acknowledgement_doc:
                item?.acknowledge?.length > 0
                  ? item.acknowledge.map((doc) => (
                      <button
                        key={doc.document_id}
                        className="text-blue-500 underline"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedFileForAckDocs(item);
                          setIsDocOpen(true);
                        }}
                      >
                        View
                      </button>
                    ))
                  : "--",
            };
          })
        );

        setTotal(data?.total_record);
      }
    } catch (error) {
      console.error("Error fetching employee report", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployeeReport({
      year: selectedYear,
      status: selectedService,
      file_status: fileStatus,
    });
  }, [currentPage, selectedYear, selectedService, fileStatus, id]);

  const cards = [
    {
      title: "Completed Tasks",
      mainValue: stats.completed_tasks_count || 0,
      icon: "/assets/icons/completed-task.svg",
      bgColor: "bg-[#cfcced]",
    },
    {
      title: "Running Tasks",
      mainValue: stats.running_tasks_count || 0,
      icon: "/assets/icons/files.svg",
      bgColor: "bg-[#cdd8ec]",
    },
    {
      title: "Files",
      icon: "/assets/icons/signature-dashboard.svg",
      bgColor: "bg-[#E5FAFF]",
      subStats: [
        { label: "In Process", value: stats.inprocess_files_count || 0 },
        { label: "Closed", value: stats.closed_files_count || 0 },
        { label: "Total", value: stats.total_files_count || 0 },
      ],
    },
    {
      title: "Invoices",
      icon: "/assets/icons/invoice-files.svg",
      bgColor: "bg-[#DDF6E8]",
      subStats: [
        { label: "Paid", value: stats.paid_invoice || 0 },
        { label: "Unpaid", value: stats.unpaid_invoice || 0 },
        { label: "Total", value: stats.total_invoice || 0 },
      ],
    },
    {
      title: "Interac e-Transfers",
      icon: "/assets/icons/money-transfer.svg",
      bgColor: "bg-[#FEF3C7]",
      subStats: [
        {
          label: "Pending",
          value: stats.unpaid_interac_e_transfer_payments || 0,
        },
        {
          label: "Completed",
          value: stats.paid_interac_e_transfer_payments || 0,
        },
        { label: "Total", value: stats.total_interac_e_transfer_payments || 0 },
      ],
    },
  ];

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination?.current);
  };

  return (
    <div>
      {employeeData && (
        <div className="mb-6 p-4 bg-white shadow rounded-lg">
          <h2 className="text-lg font-bold mb-2">Employee Information</h2>
          <p>
            <b>Name:</b> {employeeData?.name}
          </p>
          <p>
            <b>Email:</b> {employeeData?.email}
          </p>
          <p>
            <b>Mobile No:</b> {employeeData?.mobile_no || "--"}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
        {cards.map((card, idx) => (
          <DashboardCard key={idx} {...card} navigate={navigate} />
        ))}
      </div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:my-5 mt-4 justify-end">
        {/* <Search handleSearch={fetchEmployeeReport} /> */}
        <YearPicker
          value={selectedYear}
          onChange={(year) => setSelectedYear(year)}
        />

        <Select
          placeholder="Select status"
          className="md:w-[200px] w-40"
          options={[
            { label: "Completed Tasks File", value: "complete" },
            { label: "Running Tasks File", value: "running" },
          ]}
          value={selectedService}
          onChange={(value) => setSelectedService(value)}
          allowClear
        />

        <Select
          placeholder="Select file status"
          className="md:w-[200px] w-40"
          options={[
            { label: "In Process", value: "inprocess" },
            { label: "Closed", value: "closed" },
          ]}
          value={fileStatus}
          onChange={(value) => setFileStatus(value)}
          allowClear
        />
      </div>

      <div className="flex flex-col xl:flex-row gap-5 sm:mt-5 mt-4">
        <div className="md:col-span-2 xl:col-span-3 bg-white border rounded-xl p-4 overflow-x-auto w-full">
          <div className="flex items-center justify-between sub-title mb-4">
            <h3 className="title">Assigned File</h3>
          </div>

          <SkeletonTable
            columns={columns}
            data={recentFiles}
            isLoading={isLoading}
            pagination={{
              current: currentPage,
              total: total,
              pageSize: 10,
              showSizeChanger: false,
            }}
            handleTableChange={handleTableChange}
          />

          <ViewDocModal
            isDocOpen={isDocOpen}
            setIsDocOpen={setIsDocOpen}
            selectedFileForAckDocs={selectedFileForAckDocs}
          />
        </div>
      </div>
    </div>
  );
};

const DashboardCard = ({
  title,
  mainValue,
  subStats,
  icon,
  bgColor,
  redirectPath,
  navigate,
}) => (
  <div
    className="p-4 bg-white border rounded-xl shadow hover:shadow-md transition flex flex-col cursor-pointer"
    onClick={() => navigate(redirectPath)}
  >
    <div className="flex justify-between">
      <p className="text-[18px] font-bold">{title}</p>
      {icon && (
        <div
          className={`${bgColor} rounded-lg size-10 flex items-center justify-center`}
        >
          <img src={icon} alt="icon" className="w-6 h-6" />
        </div>
      )}
    </div>
    {subStats ? (
      <div className="flex justify-between mt-3">
        {subStats.map((stat, idx) => (
          <div key={idx} className="flex flex-col items-center">
            <p className="text-xl font-semibold text-gray-800">{stat.value}</p>
            <p className="text-xs font-medium">{stat.label}</p>
          </div>
        ))}
      </div>
    ) : (
      <p className="card-count mt-3">{mainValue}</p>
    )}
  </div>
);

export default EmployeeReport;
